import { AspectRatio, Box, Flex, Text, IconButton } from '@chakra-ui/react'
import { faArrowUpFromBracket } from '@fortawesome/pro-regular-svg-icons'
import { ItListRelationship, ItListStatus, User } from 'types/graphql'

import { routes } from '@redwoodjs/router'

import Placeholder from 'src/assets/image/PlaceholderGenericSVG'
import useShare from 'src/lib/hooks/useShare'
import { getAbsoluteUrl, getBrandColor, slugify } from 'src/lib/utils'
import { itListTheme } from 'src/theme'

import LikeButton from '../buttons/LikeButton/LikeButton'
import Icon from '../Icon/Icon'
import ItListImage from '../ItListImage/ItListImage'

import ContextMenu from './ContextMenu/ContextMenu'
import Cost from './Cost/Cost'
import StatusAsExplorer from './StatusAsExplorer/StatusAsExplorer'
import StatusAsOwner from './StatusAsOwner/StatusAsOwner'
import UnlistedTag from './UnlistedTag/UnlistedTag'

type ItListCardProps = {
  id: number
  coverUrl?: string
  name?: string
  status?: ItListStatus
  relationship?: ItListRelationship
  updatedAt?: string
  liked?: boolean
  unlisted?: boolean
  price?: number
  purchaseDate?: string
  creator?: Pick<
    User,
    'username' | 'id' | 'email' | 'brandColorBrightness' | 'brandHue'
  >
  showUsername?: boolean
  onClick?: React.MouseEventHandler<HTMLDivElement>
  isPlain?: boolean
  size?: 'sm' | 'lg'
}

const ItListCard = ({
  id,
  coverUrl,
  name,
  status,
  relationship,
  updatedAt,
  showUsername,
  liked,
  unlisted = false,
  price,
  purchaseDate,
  creator,
  onClick,
  isPlain = false,
  size = 'lg',
}: ItListCardProps) => {
  const share = useShare()

  const clickShareHandler: React.MouseEventHandler = async (ev) => {
    ev.stopPropagation()
    ev.preventDefault()
    await share(getAbsoluteUrl(routes.itList({ id, title: slugify(name) })), {
      owningCreatorEmail: creator.email,
      owningCreatorId: `${creator.id}`,
      owningCreatorUsername: creator.username,
      isOwner: true,
      productId: `${id}`,
    })
  }
  const isOwner = relationship === 'owner'

  return (
    <Box w="100%">
      <Box
        position="relative"
        mb={3}
        borderRadius={12}
        onClick={onClick}
        overflow="hidden"
        transition="all 0.2s ease"
        data-testid={`ItListCard-${id}`}
        _hover={{
          boxShadow: isPlain ? 'none' : itListTheme.shadows.md,
        }}
      >
        <AspectRatio ratio={16 / 9}>
          {coverUrl ? (
            <ItListImage
              htmlWidth={368}
              w="100%"
              src={coverUrl}
              alt={`Cover image for ${name}`}
            />
          ) : (
            <Placeholder />
          )}
        </AspectRatio>

        {!isPlain && (
          <Flex position="absolute" right={2} top={2} gap={3}>
            {status === 'published' && isOwner && (
              <IconButton
                aria-label="Share icon button"
                type="button"
                onClick={clickShareHandler}
                boxSize={26}
                icon={
                  <Icon
                    icon={faArrowUpFromBracket}
                    display="flex"
                    fontSize={12}
                    justifyContent="center"
                    alignItems="center"
                  />
                }
                display="flex"
                justifyContent="center"
                alignItems="center"
                variant={'roundWhite'}
              />
            )}
            {isOwner ? (
              <ContextMenu itList={{ id, status, name }} />
            ) : (
              <LikeButton
                itListId={id}
                liked={liked}
                trackingData={{
                  isOwner: false,
                  owningCreatorEmail: creator.email,
                  owningCreatorId: `${creator.id}`,
                  owningCreatorUsername: creator.username,
                  productId: `${id}`,
                }}
              />
            )}
          </Flex>
        )}
        <Flex left={2} bottom={2} position="absolute" gap={2}>
          {(!isPlain || !!purchaseDate) && (
            <Cost purchaseDate={purchaseDate} price={price} />
          )}
          {isOwner && unlisted && <UnlistedTag />}
        </Flex>
      </Box>
      {size === 'lg' ? (
        <h5 className="thick" data-testid="ItListCard-title">
          {name || (
            <Box as="span" color="sandstonePlus">
              Untitled ItList...
            </Box>
          )}
        </h5>
      ) : (
        <Text className="body-small-emphasis">{name}</Text>
      )}

      {!isPlain && (
        <Box className="body-extra-small-emphasis" textColor="stone" mt={1}>
          {isOwner ? (
            <StatusAsOwner status={status} updatedAt={updatedAt} />
          ) : (
            <StatusAsExplorer
              brandColor={getBrandColor(creator)}
              updatedAt={updatedAt}
              username={creator?.username}
              showUsername={showUsername}
            />
          )}
        </Box>
      )}
    </Box>
  )
}

export default ItListCard
